import React, { useCallback, useEffect, useState } from 'react';
import { cachedGeo, TimeZoneSelect } from '../JobFieldInputs';
import { AddressSearch, zoneLabel } from './AddressSearch';
import { api, btnPrimary, btnSecondary, card, ConfirmDialog, InlineError, input, labelCls, Skeleton, Tag, useToast } from '../ui/Kit';

const TEMPLATE_NAME = { starter: 'Starter', growth: 'Growth', enterprise: 'Enterprise' };

// Bundle 4f: what the one-step Alex screen uses (it asks the recruiter only for the time + time zone)
function AlexDefaults() {
  const toast = useToast();
  const [f, setF] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api('/api/org/alex-defaults').then(setF).catch((e) => setError(e.message)); }, []);
  const save = async () => {
    setError('');
    try { setF(await api('/api/org/alex-defaults', { method: 'PUT', body: f })); toast.show('Alex screen defaults saved'); } catch (e) { setError(e.message); }
  };
  if (!f) return error ? <InlineError>{error}</InlineError> : <Skeleton className="h-24 w-full" />;
  return (
    <section className={card + ' p-5'} data-testid="alex-defaults">
      <h2 className="text-base font-extrabold">Alex screen defaults</h2>
      <p className="text-sm text-[#64748B] mb-3">Used for every Quali5d AI screen scheduled with “Schedule Alex screen”.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block"><span className={labelCls}>Duration</span>
          <select className={input} value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: Number(e.target.value) })} data-testid="alex-duration">
            {[45, 60, 75].map((m) => <option key={m} value={m}>{m} minutes</option>)}</select></label>
        <label className="block"><span className={labelCls}>Coding</span>
          <select className={input} value={f.coding_mode} onChange={(e) => setF({ ...f, coding_mode: e.target.value })} data-testid="alex-coding">
            <option value="verbal_pseudocode">Verbal / pseudocode</option><option value="live_coding">Live coding</option></select></label>
        <label className="block"><span className={labelCls}>Proctoring</span>
          <select className={input} value={f.proctoring_strictness} onChange={(e) => setF({ ...f, proctoring_strictness: e.target.value })} data-testid="alex-proctoring">
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></label>
      </div>
      <div className="flex justify-end mt-3"><button className={btnPrimary} onClick={save} data-testid="alex-defaults-save">Save defaults</button></div>
      <InlineError>{error}</InlineError>
    </section>
  );
}

// Part B: an optional label for the company's video provider (invites say "Join on Microsoft Teams", …)
function InterviewSettings() {
  const toast = useToast();
  const [f, setF] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api('/api/org/interview-settings').then(setF).catch((e) => setError(e.message)); }, []);
  const save = async (v) => {
    setError('');
    try { setF(await api('/api/org/interview-settings', { method: 'PUT', body: { meeting_provider: v || null } })); toast.show('Interview settings saved'); } catch (e) { setError(e.message); }
  };
  if (!f) return error ? <InlineError>{error}</InlineError> : <Skeleton className="h-16 w-full" />;
  return (
    <section className={card + ' p-5'} data-testid="interview-settings">
      <h2 className="text-base font-extrabold">Interview meetings</h2>
      <p className="text-sm text-[#64748B] mb-3">Each interviewer can save a personal meeting link under My settings; it pre-fills the video rounds they lead.</p>
      <label className="block max-w-xs"><span className={labelCls}>Default video provider (optional label)</span>
        <select className={input} value={f.meeting_provider || ''} onChange={(e) => save(e.target.value)} data-testid="meeting-provider">
          <option value="">Not set</option>{Object.entries(f.providers || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
      <InlineError>{error}</InlineError>
    </section>
  );
}

function Section({ title, hint, children }) {
  return (
    <section className={card + ' p-5'}>
      <h3 className="text-base font-extrabold text-[#0F172A]">{title}</h3>
      {hint && <p className="text-sm text-[#64748B] mt-0.5 mb-4">{hint}</p>}
      {children}
    </section>
  );
}

function Profile({ setup, save }) {
  const [f, setF] = useState(setup.profile);
  useEffect(() => setF(setup.profile), [setup.profile]);
  const suggested = (setup.size_bands.find((b) => b.value === f.size_band) || {}).template;
  return (
    <Section title="Company profile" hint="Jessica uses this to suggest target companies and the market checks">
      <div className="grid md:grid-cols-3 gap-4">
        <label className="block"><span className={labelCls}>Industry / domain</span>
          <input className={input} value={f.industry || ''} onChange={(e) => setF({ ...f, industry: e.target.value })} placeholder="e.g. Fintech" /></label>
        <label className="block"><span className={labelCls}>Company size</span>
          <select className={input} value={f.size_band || ''} onChange={(e) => setF({ ...f, size_band: e.target.value || null })}>
            <option value="">Choose…</option>
            {setup.size_bands.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select></label>
        <label className="block"><span className={labelCls}>Website</span>
          <input className={input} value={f.website || ''} onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="company.com" /></label>
        <label className="block md:col-span-3 md:max-w-sm"><span className={labelCls}>Company time zone</span>
          <TimeZoneSelect className={input} ariaLabel="Company time zone" value={f.timezone || ''} onChange={(v) => setF({ ...f, timezone: v })} />
          <span className="block text-xs text-[#64748B] mt-1">Approval reminders and escalations count business days (Mon–Fri) in this time zone.</span></label>
      </div>
      <div className="flex items-center justify-between mt-4">
        <p className="text-sm text-[#64748B]">{suggested ? <>Suggested approval workflow: <strong className="text-[#0F172A]">{TEMPLATE_NAME[suggested]}</strong></> : ''}</p>
        <button className={btnPrimary} onClick={() => save('/api/org/profile', 'PUT', { industry: f.industry, website: f.website, size_band: f.size_band, timezone: f.timezone }, 'Profile saved')}>Save profile</button>
      </div>
    </Section>
  );
}

function Departments({ setup, save, ask }) {
  const [name, setName] = useState('');
  const [parent, setParent] = useState('');
  const [edit, setEdit] = useState(null);         // {id, name, head_user_id, parent_id}
  const byParent = {};
  setup.departments.forEach((d) => { (byParent[d.parent_id || 'root'] = byParent[d.parent_id || 'root'] || []).push(d); });
  const ordered = [];
  const walk = (key, depth) => (byParent[key] || []).forEach((d) => { ordered.push({ ...d, depth }); if (depth < 6) walk(d.id, depth + 1); });
  walk('root', 0);
  setup.departments.forEach((d) => { if (!ordered.find((o) => o.id === d.id)) ordered.push({ ...d, depth: 0 }); });
  return (
    <Section title="Departments" hint={'Each department can have a head (used in "Department head" approval steps)'}>
      <ul className="divide-y divide-[#E6E9F0] border border-[#E6E9F0] rounded-lg">
        {!ordered.length && <li className="px-4 py-6 text-sm text-[#64748B] text-center">No departments yet — add the first one below.</li>}
        {ordered.map((d) => (
          <li key={d.id} className="px-4 py-3" style={{ paddingLeft: 16 + d.depth * 24 }}>
            {edit && edit.id === d.id ? (
              <div className="grid md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
                <label className="block"><span className={labelCls}>Name</span><input className={input} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></label>
                <label className="block"><span className={labelCls}>Head</span>
                  <select className={input} value={edit.head_user_id || ''} onChange={(e) => setEdit({ ...edit, head_user_id: Number(e.target.value) || null })}>
                    <option value="">No head</option>{setup.people.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}</select></label>
                <label className="block"><span className={labelCls}>Part of</span>
                  <select className={input} value={edit.parent_id || ''} onChange={(e) => setEdit({ ...edit, parent_id: Number(e.target.value) || null })}>
                    <option value="">— (top level)</option>{setup.departments.filter((x) => x.id !== d.id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>
                <div className="flex gap-2"><button className={btnSecondary} onClick={() => setEdit(null)}>Cancel</button>
                  <button className={btnPrimary} onClick={async () => { if (await save(`/api/org/departments/${d.id}`, 'PATCH', { name: edit.name, head_user_id: edit.head_user_id, parent_id: edit.parent_id }, 'Department saved')) setEdit(null); }}>Save</button></div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm"><span className="font-semibold text-[#0F172A]">{d.name}</span>
                  {d.parent_id ? <span className="text-[#64748B]"> · sub-department</span> : null}
                  <span className="text-[#64748B]"> · {d.head_name ? `Head: ${d.head_name}` : 'No head set'} · {d.people} {d.people === 1 ? 'person' : 'people'}</span></p>
                <div className="flex gap-3 text-sm font-semibold">
                  <button className="text-[#0066CC] hover:underline" onClick={() => setEdit({ id: d.id, name: d.name, head_user_id: d.head_user_id, parent_id: d.parent_id })} aria-label={`Edit ${d.name}`}>Edit</button>
                  <button className="text-red-700 hover:underline" aria-label={`Delete ${d.name}`}
                    onClick={() => ask(`Delete ${d.name}?`, `${d.people ? `${d.people} ${d.people === 1 ? 'person loses' : 'people lose'} their department and` : 'Its'} sub-departments move up a level.`,
                      () => save(`/api/org/departments/${d.id}`, 'DELETE', undefined, 'Department deleted'))}>Delete</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="grid md:grid-cols-[1fr_1fr_auto] gap-2 mt-3 items-end">
        <label className="block"><span className={labelCls}>New department</span><input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Data Platform" /></label>
        <label className="block"><span className={labelCls}>Part of (optional)</span>
          <select className={input} value={parent} onChange={(e) => setParent(e.target.value)}>
            <option value="">— (top level)</option>{setup.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></label>
        <button className={btnPrimary} disabled={!name.trim()} onClick={async () => { if (await save('/api/org/departments', 'POST', { name: name.trim(), parent_id: parent ? Number(parent) : null }, 'Department added')) { setName(''); setParent(''); } }}>Add</button>
      </div>
    </Section>
  );
}

function Levels({ setup, save, ask }) {
  const [rows, setRows] = useState(setup.levels);
  const [drag, setDrag] = useState(null);
  const [name, setName] = useState('');
  const [hint, setHint] = useState('');
  useEffect(() => setRows(setup.levels), [setup.levels]);
  const persist = (next) => { setRows(next); save('/api/org/levels/order', 'PUT', { ids: next.map((l) => l.id) }, 'Order saved'); };
  const move = (i, by) => { const j = i + by; if (j < 0 || j >= rows.length) return; const next = [...rows]; [next[i], next[j]] = [next[j], next[i]]; persist(next); };
  return (
    <Section title="Job levels" hint="Drag to order, junior at the top">
      <ol className="space-y-2" aria-label="Job levels, in order">
        {!rows.length && <li className="text-sm text-[#64748B]">No levels yet — the job form offers standard ones until you add yours.</li>}
        {rows.map((l, i) => (
          <li key={l.id} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (drag === null || drag === i) return; const next = [...rows]; const [x] = next.splice(drag, 1); next.splice(i, 0, x); setDrag(null); persist(next); }}
            className="flex items-center gap-3 border border-[#E6E9F0] rounded-lg px-3 py-2 bg-white">
            <span aria-hidden="true" className="cursor-grab text-[#94A3B8] select-none">⋮⋮</span>
            <span className="text-sm flex-1"><span className="font-semibold text-[#0F172A]">{l.name}</span>{l.years_hint ? <span className="text-[#64748B]"> · {l.years_hint}</span> : null}</span>
            <button className="text-[#64748B] px-1 disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${l.name} up`}>↑</button>
            <button className="text-[#64748B] px-1 disabled:opacity-30" disabled={i === rows.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${l.name} down`}>↓</button>
            <button className="text-red-700 text-sm font-semibold hover:underline" aria-label={`Delete ${l.name}`}
              onClick={() => ask(`Delete ${l.name}?`, 'People on this level keep everything else; their level is cleared.', () => save(`/api/org/levels/${l.id}`, 'DELETE', undefined, 'Level deleted'))}>Delete</button>
          </li>
        ))}
      </ol>
      <div className="grid md:grid-cols-[1fr_200px_auto] gap-2 mt-3 items-end">
        <label className="block"><span className={labelCls}>New level</span><input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Level 3 · Senior" /></label>
        <label className="block"><span className={labelCls}>Experience (optional)</span><input className={input} value={hint} onChange={(e) => setHint(e.target.value)} placeholder="5–8 yrs" /></label>
        <button className={btnPrimary} disabled={!name.trim()} onClick={async () => { if (await save('/api/org/levels', 'POST', { name: name.trim(), years_hint: hint.trim() || null }, 'Level added')) { setName(''); setHint(''); } }}>Add</button>
      </div>
    </Section>
  );
}

export function useCountries() {
  const [countries, setCountries] = useState([]);
  useEffect(() => { cachedGeo('/countries').then((r) => setCountries(r.countries || [])).catch(() => {}); }, []);
  return countries;
}

export function locationLabel(x, countries) {
  const cn = ((countries || []).find((c) => c.code === x.country) || {}).name || x.country;
  return [x.city, x.state, cn].filter(Boolean).join(', ');
}

function LocationForm({ value, onCancel, onSave }) {
  const [f, setF] = useState(value);
  const [states, setStates] = useState([]);
  const [avail, setAvail] = useState(false);             // Part E: the address search (needs AZURE_MAPS_KEY on the server)
  const [manual, setManual] = useState(false);
  const [quiet, setQuiet] = useState('');
  const [changeZone, setChangeZone] = useState(false);
  const countries = useCountries();
  useEffect(() => { api('/api/geo/address/status').then((r) => setAvail(!!(r && r.available))).catch(() => setAvail(false)); }, []);
  useEffect(() => {
    if (!f.country) { setStates([]); return; }
    cachedGeo(`/states?country=${f.country}`).then((r) => setStates(r.states || [])).catch(() => setStates([]));
  }, [f.country]);
  const searching = avail && !manual;
  useEffect(() => {                                     // manual entry: the zone is derived from the city, unless set by hand
    if (searching || f.timezone_override || !f.country || !(f.city || '').trim()) return undefined;
    const t = setTimeout(() => {
      api(`/api/geo/timezone?country=${encodeURIComponent(f.country)}&state=${encodeURIComponent(f.state || '')}&city=${encodeURIComponent(f.city.trim())}`)
        .then((r) => { if (r && r.timezone) setF((x) => ({ ...x, timezone: r.timezone, timezone_override: false })); }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [searching, f.country, f.state, f.city, f.timezone_override]); // eslint-disable-line react-hooks/exhaustive-deps
  const picked = (r) => setF((x) => ({ ...x, address: r.formatted || [r.street, r.city].filter(Boolean).join(', '), city: r.city || x.city, state: r.state || '',
    country: r.country || x.country, postal_code: r.postal_code || '', lat: r.lat, lon: r.lon, timezone: r.timezone || x.timezone,
    timezone_override: false, _picked: true, _state_name: r.state_name, _country_name: r.country_name }));
  const countryName = f._country_name || ((countries.find((c) => c.code === f.country) || {}).name);
  const summary = [f.city, f._state_name || f.state, f.postal_code, countryName].filter(Boolean).join(', ');
  return (
    <div className="border border-[#E6E9F0] rounded-lg p-3 bg-[#F8FAFC] space-y-2">
      {searching && <AddressSearch onPick={picked} onUnavailable={() => { setAvail(false); setQuiet('Address search isn’t available right now — enter the address manually.'); }} />}
      {quiet && <p className="text-xs text-[#64748B]" role="status" data-testid="address-quiet">{quiet}</p>}
      {avail && <button type="button" className="text-xs font-semibold text-[#0066CC] hover:underline" onClick={() => setManual(!manual)} data-testid="address-manual-toggle">
        {manual ? 'Search address instead' : 'Enter manually'}</button>}
      <div className="grid md:grid-cols-3 gap-2">
        {searching ? (
          <p className="md:col-span-3 text-sm text-[#334155]" data-testid="address-summary">{summary || 'No address picked yet — search above.'}</p>
        ) : (<>
          <label className="block"><span className={labelCls}>Country</span>
            <select className={input} value={f.country || ''} onChange={(e) => setF({ ...f, country: e.target.value, state: '' })}>
              <option value="">Country…</option>{countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}</select></label>
          <label className="block"><span className={labelCls}>State / region</span>
            <select className={input} value={f.state || ''} disabled={!states.length} onChange={(e) => setF({ ...f, state: e.target.value })}>
              <option value="">{states.length ? 'State…' : '—'}</option>{states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}</select></label>
          <label className="block"><span className={labelCls}>City</span><input className={input} value={f.city || ''} onChange={(e) => setF({ ...f, city: e.target.value })} /></label>
        </>)}
        <label className="block"><span className={labelCls}>Label (optional)</span><input className={input} value={f.label || ''} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Headquarters" /></label>
        <div className="block md:col-span-1" data-testid="office-zone">
          <span className={labelCls}>Time zone</span>
          {changeZone ? <TimeZoneSelect className={input} value={f.timezone || ''} onChange={(v) => setF({ ...f, timezone: v, timezone_override: true })} />
            : <p className="text-sm text-[#334155] py-2" data-testid="office-zone-line">{f.timezone ? zoneLabel(f.timezone) : <span className="text-[#94A3B8]">Worked out from the address</span>}
              {' '}<button type="button" className="text-xs font-semibold text-[#0066CC] hover:underline" onClick={() => setChangeZone(true)} data-testid="office-zone-change">Change</button></p>}
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold self-end pb-2"><input type="checkbox" checked={!!f.is_default} onChange={(e) => setF({ ...f, is_default: e.target.checked })} />Default location</label>
        <label className="block md:col-span-2"><span className={labelCls}>Full address (for on-site interviews)</span><input className={input} value={f.address || ''} maxLength={300} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Street, city, postcode" data-testid="office-address" /></label>
        <label className="block"><span className={labelCls}>Postal code</span><input className={input} value={f.postal_code || ''} maxLength={20} onChange={(e) => setF({ ...f, postal_code: e.target.value })} data-testid="office-postal" /></label>
        <label className="block md:col-span-2"><span className={labelCls}>Parking / entry notes</span><input className={input} value={f.entry_notes || ''} maxLength={2000} onChange={(e) => setF({ ...f, entry_notes: e.target.value })} data-testid="office-entry" /></label>
        <label className="block"><span className={labelCls}>Reception contact</span><input className={input} value={f.reception_contact || ''} maxLength={200} onChange={(e) => setF({ ...f, reception_contact: e.target.value })} placeholder="Name · phone" data-testid="office-reception" /></label>
        <div className="md:col-span-3 flex justify-end gap-2">
          <button className={btnSecondary} onClick={onCancel}>Cancel</button>
          <button className={btnPrimary} disabled={!f.country || !(f.city || '').trim()} onClick={() => {
            const { _picked, _state_name, _country_name, ...body } = f;       // eslint-disable-line no-unused-vars
            onSave(body);
          }}>Save location</button>
        </div>
      </div>
    </div>
  );
}

function Locations({ setup, save, ask }) {
  const countries = useCountries();
  const [edit, setEdit] = useState(null);          // 'new' | location
  const blank = { country: 'US', state: '', city: '', label: '', timezone: '', is_default: !setup.locations.length };
  return (
    <Section title="Locations" hint="Offices offered first in the job form's location picker — and for on-site interviews (address, entry notes, reception)">
      <ul className="divide-y divide-[#E6E9F0] border border-[#E6E9F0] rounded-lg">
        {!setup.locations.length && <li className="px-4 py-6 text-sm text-[#64748B] text-center">No offices yet.</li>}
        {setup.locations.map((x) => (
          <li key={x.id} className="px-4 py-3">
            {edit && edit.id === x.id ? (
              <LocationForm value={x} onCancel={() => setEdit(null)} onSave={async (f) => { if (await save(`/api/org/locations/${x.id}`, 'PATCH', f, 'Location saved')) setEdit(null); }} />
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm"><span className="font-semibold text-[#0F172A]">{locationLabel(x, countries)}</span>
                  <span className="text-[#64748B]">{x.label ? ` · ${x.label}` : ''}{x.timezone ? ` · ${x.timezone}` : ''}{x.address ? ` · ${x.address}` : ''}</span>
                  {x.is_default && <span className="ml-2"><Tag tone="green">Default</Tag></span>}</p>
                <div className="flex gap-3 text-sm font-semibold">
                  <button className="text-[#0066CC] hover:underline" onClick={() => setEdit(x)}>Edit</button>
                  <button className="text-red-700 hover:underline" onClick={() => ask('Remove this location?', 'Jobs already using it keep their location.', () => save(`/api/org/locations/${x.id}`, 'DELETE', undefined, 'Location removed'))}>Remove</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-3">
        {edit === 'new' ? <LocationForm value={blank} onCancel={() => setEdit(null)} onSave={async (f) => { if (await save('/api/org/locations', 'POST', f, 'Location added')) setEdit(null); }} />
          : <button className="text-sm font-semibold text-[#0066CC] hover:underline" onClick={() => setEdit('new')}>+ Add location</button>}
      </div>
    </Section>
  );
}

export default function OrgSetupTab() {
  const toast = useToast();
  const [setup, setSetup] = useState(null);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => api('/api/org/setup').then(setSetup).catch((e) => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (path, method, body, msg) => {
    setError('');
    try { setSetup(await api(path, { method, body })); toast.show(msg); return true; } catch (e) { setError(e.message); toast.show(e.message, 'error'); return false; }
  };
  const ask = (title, message, run) => setConfirm({ title, message, run });
  if (!setup) return error ? <InlineError>{error}</InlineError> : <div className="space-y-3"><Skeleton className="h-32 w-full" /><Skeleton className="h-48 w-full" /></div>;
  return (
    <div className="space-y-5">
      <InlineError>{error}</InlineError>
      <Profile setup={setup} save={save} />
      <div className="grid xl:grid-cols-2 gap-5">
        <Departments setup={setup} save={save} ask={ask} />
        <Levels setup={setup} save={save} ask={ask} />
      </div>
      <Locations setup={setup} save={save} ask={ask} />
      <AlexDefaults />
      <InterviewSettings />
      <ConfirmDialog open={!!confirm} title={confirm && confirm.title} message={confirm && confirm.message} danger confirmLabel="Delete"
        onClose={() => setConfirm(null)} onConfirm={async () => { const c = confirm; setConfirm(null); await c.run(); }} />
    </div>
  );
}
